import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader, Plus, KanbanSquare, AlertCircle } from 'lucide-react';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import { useAuth } from '../../contexts/AuthContext';
import { kanbanApiService } from '../../services/kanbanApi';
import { BOARD_TEMPLATES, type BoardTemplate, type KanbanBoard } from '../../types/kanban';
import { cn } from '../../utils/cn';

export interface CreationPlanStep {
  title: string;
  detail: string;
}

/** Deterministic steps for progress UI (must match `handleSubmit` request order). */
export function buildBoardCreationPlan(template: BoardTemplate): CreationPlanStep[] {
  const steps: CreationPlanStep[] = [
    {
      title: 'Creating board',
      detail: 'Sending your team, title, and board settings to the server.',
    },
  ];

  const lists = [...template.lists].sort((a, b) => a.position - b.position);
  for (const list of lists) {
    steps.push({
      title: `Adding list: ${list.title}`,
      detail: 'Creating the column on your new board.',
    });
  }

  const examples = template.exampleCards;
  if (!examples?.length) {
    steps.push({
      title: 'Finishing up',
      detail: 'Saving your board and returning to the workspace list.',
    });
    return steps;
  }

  const listCount = lists.length;
  for (const ex of examples) {
    if (ex.listIndex < 0 || ex.listIndex >= listCount) continue;

    steps.push({
      title: `Adding example card: ${ex.title}`,
      detail: 'Seeding a sample card so you can see how the board looks.',
    });

    if (ex.is_blocked) {
      steps.push({
        title: 'Applying blocked state',
        detail: 'Marking the sample card as blocked (if the server did not already).',
      });
    }

    if (ex.checklists) {
      for (const cl of ex.checklists) {
        steps.push({
          title: `Adding checklist: ${cl.title}`,
          detail: 'Creating a checklist on the sample card.',
        });
        for (const item of cl.items) {
          steps.push({
            title: 'Adding checklist item',
            detail: item.content.length > 80 ? `${item.content.slice(0, 80)}…` : item.content,
          });
          if (item.is_checked) {
            steps.push({
              title: 'Saving checklist item state',
              detail: 'Marking a sample item as done.',
            });
          }
        }
      }
    }
  }

  steps.push({
    title: 'Finishing up',
    detail: 'Opening your workspace list with the new board.',
  });

  return steps;
}

const teamColors: Record<string, string> = {
  devteam: '#3498DB',
  buildteam: '#E67E22',
  eventteam: '#2ECC71',
};

export interface BoardCreatedContext {
  formTitle: string;
  formTeam: string;
  formBackground: string;
  formDescription: string | undefined;
  creatorUuid: string;
}

interface CreateBoardModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (board: KanbanBoard, context: BoardCreatedContext) => void;
  teams: string[];
  defaultTeam?: string;
}

function templateAccentColor(template: BoardTemplate): string {
  if (template.team && teamColors[template.team]) {
    return teamColors[template.team];
  }
  return 'rgba(148, 163, 184, 0.5)';
}

export const CreateBoardModal = ({
  isOpen,
  onClose,
  onCreated,
  teams,
  defaultTeam,
}: CreateBoardModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const { identity } = useAuth();

  const [team, setTeam] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [backgroundColor, setBackgroundColor] = useState('#334155');
  const [selectedTemplateIndex, setSelectedTemplateIndex] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [creationProgress, setCreationProgress] = useState<{
    stepIndex: number;
    totalSteps: number;
    title: string;
    detail: string;
  } | null>(null);

  const handleClickOutside = useCallback(
    (_e: MouseEvent | TouchEvent) => {
      if (isSubmitting) return;
      onClose();
    },
    [isSubmitting, onClose],
  );
  useOnClickOutside(modalRef, handleClickOutside);

  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    setIsSubmitting(false);
    setCreationProgress(null);
    setTitle('');
    setDescription('');
    setBackgroundColor('#334155');

    const initialTeam =
      defaultTeam && teams.includes(defaultTeam) ? defaultTeam : teams[0] ?? '';
    setTeam(initialTeam);

    const tIdx = BOARD_TEMPLATES.findIndex((t) => t.team === initialTeam);
    setSelectedTemplateIndex(tIdx >= 0 ? tIdx : BOARD_TEMPLATES.length - 1);
  }, [isOpen, teams, defaultTeam]);

  const selectedTemplate = BOARD_TEMPLATES[selectedTemplateIndex] ?? BOARD_TEMPLATES[BOARD_TEMPLATES.length - 1];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      setError('Please enter a board title.');
      return;
    }
    if (!team) {
      setError('Please select a team.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    const plan = buildBoardCreationPlan(selectedTemplate);
    let nextStep = 0;
    const showStep = (index: number) => {
      const s = plan[index];
      if (!s) return;
      setCreationProgress({
        stepIndex: index,
        totalSteps: plan.length,
        title: s.title,
        detail: s.detail,
      });
    };
    const advanceAfterRequest = () => {
      nextStep += 1;
      if (nextStep < plan.length) showStep(nextStep);
    };

    showStep(0);

    try {
      const board = await kanbanApiService.createBoard({
        team,
        title: trimmedTitle,
        description: description.trim() || undefined,
        background_color: backgroundColor.trim() || undefined,
      });
      advanceAfterRequest();

      const lists = [...selectedTemplate.lists].sort((a, b) => a.position - b.position);
      const createdListIds: string[] = [];
      for (const list of lists) {
        const created = await kanbanApiService.createList(board.id, {
          title: list.title,
          position: list.position,
          wip_limit: list.wip_limit ?? null,
          list_role: list.list_role,
        });
        createdListIds.push(created.id);
        advanceAfterRequest();
      }

      const examples = selectedTemplate.exampleCards;
      if (examples && examples.length > 0) {
        const nextPosition = new Map<string, number>();
        for (const ex of examples) {
          const listId = createdListIds[ex.listIndex];
          if (!listId) continue;
          const position = nextPosition.get(listId) ?? 0;
          nextPosition.set(listId, position + 1);
          const card = await kanbanApiService.createCard(board.id, {
            list_id: listId,
            title: ex.title,
            description: ex.description,
            position,
            priority: ex.priority,
            card_type: ex.card_type,
            class_of_service: ex.class_of_service,
            due_date: ex.due_date,
            start_date: ex.start_date,
            cover_color: ex.cover_color ?? undefined,
            is_blocked: ex.is_blocked,
            blocked_reason: ex.blocked_reason ?? undefined,
          });
          advanceAfterRequest();
          if (ex.is_blocked) {
            if (card.is_blocked !== 1) {
              try {
                await kanbanApiService.blockCard(card.id, {
                  is_blocked: true,
                  blocked_reason: ex.blocked_reason ?? 'Blocked',
                });
              } catch {
                /* optional */
              }
            }
            advanceAfterRequest();
          }
          if (ex.checklists) {
            for (const cl of ex.checklists) {
              const checklist = await kanbanApiService.createChecklist(card.id, { title: cl.title });
              advanceAfterRequest();
              for (const item of cl.items) {
                const row = await kanbanApiService.createChecklistItem(checklist.id, { content: item.content });
                advanceAfterRequest();
                if (item.is_checked) {
                  await kanbanApiService.toggleChecklistItem(row.id, true);
                  advanceAfterRequest();
                }
              }
            }
          }
        }
      }

      onCreated(board, {
        formTitle: trimmedTitle,
        formTeam: team,
        formBackground: backgroundColor.trim(),
        formDescription: description.trim() || undefined,
        creatorUuid: identity?.user_uuid ?? '',
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create board.');
    } finally {
      setCreationProgress(null);
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (!isSubmitting) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isSubmitting]);

  if (!isOpen) return null;

  const modalContent = (
    <div className="modal-overlay z-[9999]">
      <div
        ref={modalRef}
        className="modal-panel max-w-lg"
      >
        <button
          type="button"
          onClick={onClose}
          className="modal-close touch-manipulation z-10"
          aria-label="Close"
          disabled={isSubmitting}
        >
          <X className="w-5 h-5" />
        </button>

        <div className="modal-header">
          <h2 className="modal-title flex items-center gap-2">
            <KanbanSquare className="w-5 h-5 text-accent-400 shrink-0" />
            Create Board
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="alert alert-danger" role="alert">
              <AlertCircle />
              <span>{error}</span>
            </div>
          )}

          <div>
            <label htmlFor="create-board-team" className="text-xs font-medium text-glass-400 mb-1.5 block">
              Team
            </label>
            <select
              id="create-board-team"
              value={team}
              onChange={(e) => setTeam(e.target.value)}
              className="glass-input w-full"
              disabled={isSubmitting || teams.length === 0}
            >
              {teams.length === 0 ? (
                <option value="">No teams available</option>
              ) : (
                teams.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))
              )}
            </select>
          </div>

          <div>
            <label htmlFor="create-board-title" className="text-xs font-medium text-glass-400 mb-1.5 block">
              Title
            </label>
            <input
              id="create-board-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="glass-input w-full"
              placeholder="Board name"
              disabled={isSubmitting}
              required
            />
          </div>

          <div>
            <label htmlFor="create-board-description" className="text-xs font-medium text-glass-400 mb-1.5 block">
              Description
            </label>
            <textarea
              id="create-board-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="glass-input w-full min-h-[80px] resize-y"
              placeholder="Optional"
              rows={3}
              disabled={isSubmitting}
            />
          </div>

          <div>
            <label htmlFor="create-board-bg" className="text-xs font-medium text-glass-400 mb-1.5 block">
              Background color
            </label>
            <div className="flex items-center gap-3">
              <input
                id="create-board-bg"
                type="color"
                value={backgroundColor}
                onChange={(e) => setBackgroundColor(e.target.value)}
                className="h-10 w-14 cursor-pointer rounded-lg border border-glass-200/20 bg-transparent p-0.5"
                disabled={isSubmitting}
                aria-label="Background color"
              />
              <input
                type="text"
                value={backgroundColor}
                onChange={(e) => setBackgroundColor(e.target.value)}
                className="glass-input flex-1 font-mono"
                placeholder="#334155"
                disabled={isSubmitting}
              />
            </div>
          </div>

          <div>
            <span className="text-xs font-medium text-glass-400 mb-1.5 block">Template</span>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {BOARD_TEMPLATES.map((tpl, index) => {
                const selected = index === selectedTemplateIndex;
                const accent = templateAccentColor(tpl);
                return (
                  <button
                    key={tpl.name}
                    type="button"
                    disabled={isSubmitting}
                    onClick={() => setSelectedTemplateIndex(index)}
                    className={cn(
                      'subcard p-3 text-left transition-colors text-sm',
                      'hover:border-glass-200/25',
                      selected && 'border-accent-400/70 ring-1 ring-accent-400/30 bg-accent-500/5',
                    )}
                  >
                    <span
                      className="block w-full h-1 rounded-full mb-2 opacity-90"
                      style={{ backgroundColor: accent }}
                    />
                    <span className="font-medium text-white block truncate">{tpl.name}</span>
                    <span className="text-glass-400 text-xs mt-0.5 block">
                      {tpl.lists.length} {tpl.lists.length === 1 ? 'list' : 'lists'}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              onClick={onClose}
              className="glass-button-secondary"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={cn('btn-primary', isSubmitting && 'opacity-70')}
              disabled={isSubmitting || teams.length === 0}
            >
              {isSubmitting ? (
                <Loader className="w-4 h-4 animate-spin" />
              ) : (
                <Plus className="w-4 h-4" />
              )}
              Create
            </button>
          </div>
        </form>

        {creationProgress && (
          <div
            className="absolute inset-0 z-20 flex flex-col justify-center rounded-[var(--radius-card)] bg-[var(--bg-elevated)] px-5 py-6 backdrop-blur-sm border border-accent-500/20"
            role="status"
            aria-live="polite"
            aria-busy="true"
          >
            <div className="flex items-center gap-2 text-accent-300 mb-3">
              <Loader className="w-5 h-5 animate-spin shrink-0" />
              <span className="eyebrow text-accent-300">Setting up your board</span>
            </div>
            <p className="text-xs text-amber-200/95 mb-4 leading-relaxed">
              Do not close this window or refresh the page. Leaving now can interrupt the setup and leave the board
              half-created.
            </p>
            <div className="h-2 w-full rounded-full bg-[var(--surface-inset)] overflow-hidden mb-3">
              <div
                className="h-full rounded-full bg-accent-500 transition-[width] duration-300 ease-out"
                style={{
                  width: `${Math.min(100, Math.round(((creationProgress.stepIndex + 1) / creationProgress.totalSteps) * 100))}%`,
                }}
              />
            </div>
            <p className="text-[11px] text-glass-500 mb-1 tabular-nums">
              Step {creationProgress.stepIndex + 1} of {creationProgress.totalSteps}
            </p>
            <h3 className="text-base font-semibold text-white mb-1">{creationProgress.title}</h3>
            <p className="text-sm text-glass-300 leading-snug">{creationProgress.detail}</p>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};
